import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { useAuth } from "@/features/auth";
import { useApiClient } from "@/infrastructure/rest";
import { TvvEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import {
    AddTvvBusDto,
    PatchTvvBusDto,
    TvvBesteldeRouteDto,
    TvvBesteldeRoutePatchDto,
    TvvRijtijdDto,
    TvvRouteAanvraagDto,
    TvvStandaardRouteDto,
} from "@/shared/types/generated/dtos";
import { handleNotFoundAsEmpty } from "@/shared/utils";
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { TvvBesteldeRoute, TvvStandaardRoute } from "./domain";

export const TVV_STANDAARD_ROUTES_QUERY_KEY = "tvv-standaard-routes";
export const TVV_BESTELDE_ROUTES_QUERY_KEY = "tvv-besteld";

export function useGetTvvStandaardRoutes(dossierId: string) {
    const apiClient = useApiClient();
    const { apiDomain } = useAuth();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, TVV_STANDAARD_ROUTES_QUERY_KEY],
        queryFn: async () => {
            // We pass dossierId as a query parameter, as the TVV standaard routes are not part of a dossier.
            const response = await apiClient.get<TvvStandaardRouteDto[]>(`/tvv/routes?dossierId=${dossierId}`);
            return TvvStandaardRoute.sort((response ?? []).map((dto) => TvvStandaardRoute.fromDto(dto)));
        },
        // staleTime 15 minutes to prevent unnecessary refetches
        staleTime: 15 * 60 * 1000,
        enabled: apiDomain === "regievoerder",
    });
}

export function useGetTvvStandaardRoutesForStations<T>(
    dossierId: string,
    stationCodes: string[],
    options?: Omit<UseQueryOptions<TvvStandaardRoute[], Error, T>, "queryFn" | "queryKey">
) {
    const apiClient = useApiClient();
    const sortedCodes = [...stationCodes].sort((a, b) => a.localeCompare(b));

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, TVV_STANDAARD_ROUTES_QUERY_KEY, sortedCodes.join(",")],
        queryFn: async () => {
            const response = await apiClient.get<TvvStandaardRouteDto[]>(`/tvv/routes?stations=${sortedCodes.join(",")}`);
            return TvvStandaardRoute.sort((response ?? []).map((dto) => TvvStandaardRoute.fromDto(dto)));
        },
        enabled: stationCodes.length > 0,
        ...options,
    });
}

export const TVV_RIJTIJD_QUERY_KEY = "tvv-rijtijd";

export function useGetTvvRijtijd(beginStation: string, eindStation: string, options?: { enabled?: boolean }) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [TVV_RIJTIJD_QUERY_KEY, beginStation, eindStation],
        queryFn: async () => {
            const params = new URLSearchParams({ beginStation, eindStation });
            return apiClient.get<TvvRijtijdDto>(`/tvv/rijtijden?${params}`);
        },
        enabled: (options?.enabled ?? true) && beginStation.length > 0 && eindStation.length > 0,
        staleTime: Infinity,
        retry: false,
    });
}

export function useGetTvvBesteldeRoutes<T = TvvBesteldeRoute[]>(
    dossierId: string,
    options?: Omit<UseQueryOptions<TvvBesteldeRoute[], Error, T>, "queryFn" | "queryKey">
) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, dossierId, TVV_BESTELDE_ROUTES_QUERY_KEY],
        queryFn: async () => {
            try {
                const response = await apiClient.get<TvvBesteldeRouteDto[]>(`/dossiers/${dossierId}/tvv`);
                const routes = (response ?? []).map((dto) => TvvBesteldeRoute.fromDto(dto));
                return routes.sort((a, b) => a.id - b.id);
            } catch (error) {
                return handleNotFoundAsEmpty<TvvBesteldeRoute>(error);
            }
        },
        refetchOnMount: "always",
        ...options,
    });
}

export function usePostTvvBestellingen() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, bestellingen }: { dossierId: string; bestellingen: TvvRouteAanvraagDto[] }) => {
            await Promise.all(bestellingen.map((bestelling) => apiClient.post(`/dossiers/${dossierId}/tvv`, bestelling)));
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId);
        },
        onError: (error) => {
            console.error("Fout bij aanmaken bestelling:", error);
        },
    });
}

type VraagTvvRouteStopAanRequest = {
    dossierId: string;
    tvvId: number;
};

export function useVraagTvvRouteStopAan() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, tvvId }: VraagTvvRouteStopAanRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/tvv/${tvvId}`, {
                actie: "Stop",
                scope: "BesteldeRoute",
            } satisfies TvvBesteldeRoutePatchDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId);
        },
    });
}

type VraagTvvStopAlleRoutesAanRequest = {
    dossierId: string;
};

export function useVraagTvvStopAlleRoutesAan() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId }: VraagTvvStopAlleRoutesAanRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/tvv`, {
                actie: "Stop",
                scope: "AlleRoutes",
            } satisfies TvvBesteldeRoutePatchDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId);
        },
    });
}

type PatchTvvBestellingRequest = {
    dossierId: string;
    tvvId: number;
    aantalBussenOpBeginStation: number;
    busFrequentie: number;
    bussenBesteld: number;
    reisperiode: "Avondspits" | "Dal" | "Ochtendspits" | "Superdal" | "Zaterdag" | "Zondag" | "Handmatig";
};

export function usePatchTvvBestelling() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, tvvId, ...payload }: PatchTvvBestellingRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/tvv/${tvvId}`, {
                ...payload,
                actie: "Update",
                scope: "BesteldeRoute",
            } satisfies TvvBesteldeRoutePatchDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, String(dossierId));
        },
    });
}

type PatchTvvOVRouteRequest = {
    dossierId: string;
    tvvId: number;
    ovLijnNummer: string;
};

export function usePatchTvvOVRoute() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, tvvId, ...payload }: PatchTvvOVRouteRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/tvv/${tvvId}`, {
                ...payload,
                actie: "Update",
                scope: "OvLijnNummer",
            } satisfies TvvBesteldeRoutePatchDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, String(dossierId));
        },
    });
}

type PatchTvvEtaRequest = {
    dossierId: string;
    tvvId: number;
    etasPubliek: boolean;
};

export function useMarkEtasPubliek() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, tvvId, ...payload }: PatchTvvEtaRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/tvv/${tvvId}`, {
                ...payload,
                actie: "Update",
                scope: "EtasPubliek",
            } satisfies TvvBesteldeRoutePatchDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, String(dossierId));
        },
    });
}

type PostTvvBusEtaRequest = {
    dossierId: string;
    routeId: number;
    eta: AddTvvBusDto;
};

export function usePostTvvBusEta() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, routeId, eta }: PostTvvBusEtaRequest) => {
            await apiClient.post(`/dossiers/${dossierId}/tvv/${routeId}/bus`, eta satisfies AddTvvBusDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId);
        },
    });
}

type BevestigStopTvvRouteRequest = {
    dossierId: string;
    tvvId: number;
};

export function useBevestigStopTvvRoute() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, tvvId }: BevestigStopTvvRouteRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/tvv/${tvvId}`, {
                actie: "Stop",
                scope: "BesteldeRoute",
            } satisfies TvvBesteldeRoutePatchDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId);
        },
    });
}

type PatchTvvBusEtaRequest = {
    dossierId: string;
    routeId: number;
    busId: number;
    eta: string;
};

export function usePatchTvvBusEta() {
    const queryClient = useQueryClient();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ dossierId, routeId, busId, eta }: PatchTvvBusEtaRequest) => {
            await apiClient.patch(`/dossiers/${dossierId}/tvv/${routeId}/bus/${busId}`, {
                actie: "Update",
                scope: "Eta",
                tijd: eta,
            } satisfies PatchTvvBusDto);
        },
        onSuccess: async (_, { dossierId }) => {
            await invalidateOpenDossiersQueries(queryClient, dossierId);
        },
    });
}

export function useRealtimeTvv() {
    const { handleEvent } = useUpdateDossierPropertyQueriesData(TVV_BESTELDE_ROUTES_QUERY_KEY);

    useRealtimeTopic<TvvEvent>("tvv", {
        onMessage: (message) => {
            const { dossierId, routes, reason } = message;
            routes.forEach((route) => {
                handleEvent(dossierId, route.id, reason, TvvBesteldeRoute.fromDto(route));
            });
        },
    });
}
